import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import WaCampaignsAPI from 'dashboard/api/waCampaigns';

export function useWaCampaigns() {
  const campaigns = ref([]);
  const stats = ref({
    total_campaigns: 0,
    active_campaigns: 0,
    paused_campaigns: 0,
    sent_today: 0,
    daily_limit: 50,
    sent_this_week: 0,
  });
  const loading = ref(false);
  const saving = ref(false);

  const fetchCampaigns = async () => {
    loading.value = true;
    try {
      const response = await WaCampaignsAPI.get();
      campaigns.value = response.data;
    } catch (error) {
      const msg = error?.response?.data?.error || 'Error loading campaigns';
      useAlert(msg);
      console.error('Error fetching campaigns:', msg, error?.response?.data);
    } finally {
      loading.value = false;
    }
  };

  const fetchStats = async () => {
    try {
      const response = await WaCampaignsAPI.getStats();
      stats.value = response.data;
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const createCampaign = async data => {
    saving.value = true;
    try {
      const response = await WaCampaignsAPI.create(data);
      campaigns.value.unshift(response.data);
      useAlert('Campaign created successfully');
      return response.data;
    } catch (error) {
      const msg = error?.response?.data?.error || 'Error creating campaign';
      useAlert(msg);
      console.error('Error creating campaign:', msg, error?.response?.data);
      throw error;
    } finally {
      saving.value = false;
    }
  };

  const updateCampaign = async (id, data) => {
    saving.value = true;
    try {
      const response = await WaCampaignsAPI.update(id, data);
      const index = campaigns.value.findIndex(c => c.id === id);
      if (index !== -1) campaigns.value[index] = response.data;
      useAlert('Campaign updated successfully');
      return response.data;
    } catch (error) {
      useAlert('Error updating campaign');
      console.error('Error updating campaign:', error);
      throw error;
    } finally {
      saving.value = false;
    }
  };

  const deleteCampaign = async id => {
    try {
      await WaCampaignsAPI.delete(id);
      campaigns.value = campaigns.value.filter(c => c.id !== id);
      useAlert('Campaign deleted');
    } catch (error) {
      useAlert('Error deleting campaign');
      console.error('Error deleting campaign:', error);
    }
  };

  const pauseCampaign = async id => {
    try {
      const response = await WaCampaignsAPI.pause(id);
      const index = campaigns.value.findIndex(c => c.id === id);
      if (index !== -1) campaigns.value[index] = response.data;
      useAlert('Campaign paused');
      return response.data;
    } catch (error) {
      useAlert('Error pausing campaign');
      console.error('Error pausing campaign:', error);
    }
  };

  const resumeCampaign = async id => {
    try {
      const response = await WaCampaignsAPI.resume(id);
      const index = campaigns.value.findIndex(c => c.id === id);
      if (index !== -1) campaigns.value[index] = response.data;
      useAlert('Campaign resumed');
      return response.data;
    } catch (error) {
      useAlert('Error resuming campaign');
      console.error('Error resuming campaign:', error);
    }
  };

  const previewCampaign = async (id, contactId = null) => {
    try {
      const response = await WaCampaignsAPI.preview(id, contactId);
      return response.data;
    } catch (error) {
      useAlert('Error generating preview');
      console.error('Error previewing campaign:', error);
      return null;
    }
  };

  const fetchLogs = async (campaignId, params = {}) => {
    try {
      const response = await WaCampaignsAPI.getLogs(campaignId, params);
      return response.data;
    } catch (error) {
      useAlert('Error loading logs');
      console.error('Error fetching logs:', error);
      return null;
    }
  };

  return {
    campaigns,
    stats,
    loading,
    saving,
    fetchCampaigns,
    fetchStats,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    pauseCampaign,
    resumeCampaign,
    previewCampaign,
    fetchLogs,
  };
}
